/**
 * Implementation based on: https://medium.com/swlh/creating-a-simple-real-time-chat-with-net-core-reactjs-and-signalr-6367dcadd2c6
 */

import {
  HttpError,
  HubConnection,
  HubConnectionBuilder,
} from "@microsoft/signalr";
import { useCallback, useEffect, useState } from "react";

enum MessageType {
  NOTIFICATION = "NotificationStackPush",
}

type SignalRNotificationMessage = {
  id: 1049;
  title: string;
  content: string;
  level: SignalRNotificationLevel;
  addDateUtc: string;
  streamCode: string;
};
type SignalRNotificationLevel = {
  id: number;
  code: NotificationType;
  themeJson: Record<string, string>;
};
enum NotificationType {
  SUCCESS = "SUCCESS",
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR",
}

export type MessageAndHandler<T> = {
  messageCode: string;
  handler: (messageData: T) => void;
};

// Make T a union type if there are multiple additionalMessageHandlers with different
// message data shapes.
type Options<T> = {
  url: string;
  token: string;
  // By default hook listens for NotificationStackPush messages. Additional
  // message types and handlers can be passed in via this option.
  additionalMessageHandlers?: MessageAndHandler<T>[];
  // Optional flag that can be toggled to start and stop the connection.
  activate?: boolean;
};

type SignalRConnection = {
  hubConnection: HubConnection;
  isConnected: boolean;
  isConnecting: boolean;
};

export const useSignalRNotifications = <T = any>({
  url,
  token,
  activate = true,
  additionalMessageHandlers,
}: Options<T>) => {
  const [connection, setConnection] = useState<SignalRConnection | null>(null);
  const [
    latestMessage,
    setLatestMessage,
  ] = useState<SignalRNotificationMessage | null>(null);

  const clearLatestMessage = useCallback(() => {
    setLatestMessage(null);
  }, []);

  const createConnection = useCallback(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => {
          return token;
        },
      })
      .withAutomaticReconnect()
      .build();
    setConnection({
      hubConnection: newConnection,
      isConnected: false,
      isConnecting: false,
    });
  }, [url, token]);

  const clearConnection = useCallback((connection: HubConnection) => {
    connection.stop();
    setConnection(null);
  }, []);

  const startConnection = useCallback(
    (connection: HubConnection) => {
      connection
        .start()
        .then(() => {
          setConnection(prev => ({
            ...prev!,
            isConnecting: false,
            isConnected: true,
          }));

          connection.on(
            MessageType.NOTIFICATION,
            (data: SignalRNotificationMessage) => {
              setLatestMessage(data);
            }
          );

          additionalMessageHandlers?.forEach(type => {
            connection.on(type.messageCode, (data: T) => {
              type.handler(data);
            });
          });
        })
        .catch((err: HttpError) => {
          //TODO error handling
        });
    },
    [additionalMessageHandlers, clearConnection]
  );

  useEffect(() => {
    if (!connection && activate) {
      createConnection();
    }
  }, [createConnection, connection, activate]);

  useEffect(() => {
    if (
      connection?.hubConnection &&
      !connection?.isConnecting &&
      !connection?.isConnected
    ) {
      setConnection(prev => ({ ...prev!, isConnecting: true }));
      startConnection(connection.hubConnection);
    }
  }, [startConnection, connection]);

  useEffect(() => {
    if (!activate && connection?.isConnected) {
      clearConnection(connection.hubConnection);
    }
  }, [
    activate,
    connection?.isConnected,
    connection?.hubConnection,
    clearConnection,
  ]);

  return [latestMessage, clearLatestMessage] as const;
};
